import { z } from 'zod';
import {
  FEEDBACK_SENTIMENTS,
  FEEDBACK_REASONS,
  FEEDBACK_DOCUMENT_TYPES,
  REASON_MAP,
  FeedbackReason,
  FeedbackSentiment,
} from './feedback.types';

// Query parameters for getting feedback by document
export const getFeedbackByDocumentQueryParamsSchema = z.object({
  documentType: z.enum(FEEDBACK_DOCUMENT_TYPES),
  documentId: z.string().min(1),
});
export type GetFeedbackByDocumentQueryParams = z.infer<
  typeof getFeedbackByDocumentQueryParamsSchema
>;

// Create feedback request
export const createFeedbackBodySchema = z.object({
  documentType: z.enum(FEEDBACK_DOCUMENT_TYPES),
  documentId: z.string().min(1),
  sentiment: z.enum(FEEDBACK_SENTIMENTS),
  reasons: z.array(z.enum(FEEDBACK_REASONS)).optional(),
  comment: z.string().max(500).optional(),
});
export type CreateFeedbackRequestBody = z.infer<typeof createFeedbackBodySchema>;

// Enhanced create feedback schema with sentiment-reason validation
const validateReasonsMatchSentiment = (
  sentiment: FeedbackSentiment,
  reasons: FeedbackReason[],
): boolean => {
  return reasons.every((reason) => REASON_MAP[reason]?.sentiment === sentiment);
};
export const createFeedbackBodySchemaEnhanced = createFeedbackBodySchema.refine(
  (data) => {
    if (!data.reasons?.length) return true;
    return validateReasonsMatchSentiment(data.sentiment, data.reasons);
  },
  {
    message: 'All reasons must match the selected sentiment',
    path: ['reasons'],
  },
);
export type CreateFeedbackRequestBodyEnhanced = z.infer<typeof createFeedbackBodySchemaEnhanced>;

// Query parameters for getting feedback for a campaign (with optional filters)
export const getCampaignFeedbackQueryParamsSchema = z.object({
  reasons: z
    .string()
    .optional()
    .transform((val) => (val ? val.split(',').filter(Boolean) : undefined)),
  locationIds: z
    .string()
    .optional()
    .transform((val) => (val ? val.split(',').filter(Boolean) : undefined)),
});
export type GetCampaignFeedbackQueryParams = z.infer<typeof getCampaignFeedbackQueryParamsSchema>;

// Admin query parameters for getting all feedback for a campaign (cross-account)
export const getAdminFeedbackQueryParamsSchema = z.object({
  documentType: z.enum(FEEDBACK_DOCUMENT_TYPES),
  documentId: z.string().min(1),
  reasons: z
    .string()
    .optional()
    .transform((val) => (val ? val.split(',').filter(Boolean) : undefined)),
  locationIds: z
    .string()
    .optional()
    .transform((val) => (val ? val.split(',').filter(Boolean) : undefined)),
});
export type GetAdminFeedbackQueryParams = z.infer<typeof getAdminFeedbackQueryParamsSchema>;
